/**
 * Auth API Service
 * Service layer for authentication-related API calls
 */

import apiClient, { clearUserProfile, setUserProfile } from './axios.config';
import type { User } from '@/models/user';

// CU-0 (2026-08-09): the F2.1 tolerance (D-AUTH-1/2 — envelope unwrap +
// `/auth/profile` fallback) is REMOVED. The server ADOPTED the FE's actual
// auth paths (fullsuite plan B1.2'), so `GET /auth/me` answers the bare
// `User` in every seam state — MSW mock and the live GraphQL server alike.
// The gold REST seam no longer serves auth for this FE: boot live mode with
// the `auth` module flipped live (client.ts live-module CSV; see the
// .env.development notes).
//
// B1.3' live seam: auth rides REST-BY-ADOPTION — when the live-module switch
// (client.ts) flips `auth` live, these SAME paths (`/api/auth/*`) leave the
// mock seam and reach the GraphQL server (vite proxy routes `/api/auth`
// there; the MSW worker passes the prefix through). login/logout/profile
// need ZERO live-mode branches here. A 404 at `/auth/me` is a real defect
// and must surface, never be masked as path drift.

/**
 * Login credentials
 */
export interface LoginCredentials {
  username: string;
  password: string;
}

/**
 * Login response
 */
export interface LoginResponse {
  message: string;
  userProfile: User;
}

/**
 * Auth API service object
 */
export const authApi = {
  /**
   * POST /api/auth/login
   * Login with username and password
   * Note: Uses session-based authentication with HttpOnly cookies
   */
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
    setUserProfile(response.data.userProfile);
    return response.data;
  },

  /**
   * POST /api/auth/logout
   * Logout current user
   */
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      clearUserProfile();
    }
  },

  /**
   * GET /api/auth/me
   * Get current user profile — one path, one shape (bare `User`).
   * Errors propagate untouched: 401 rides the axios session-expired
   * interceptor, and a 404 is a real routing defect (post-CU-0 there is no
   * legacy path to fall back to).
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    setUserProfile(response.data);
    return response.data;
  },
};

export default authApi;
